const UNKNOWN_EMAIL_PARTICIPANT = 'Email User';

type GetActiveParticipantNames = {
  activeParticipantsInConversation: Array<ActiveParticipant>;
  referenceName: string;
};

/**
 * @description Returns an object containing two properties: `displayNames` and `fullParticipantList`.
 *              The `displayNames` is a string representing the names of the participants,
 *              formatted for display. May include a count of unknown or obfuscated participants.
 *              The `fullParticipantList` is an array of strings representing the full list of participant names,
 *              including both known and unknown/obfuscated participants. Unknown/obfuscated participants are represented by an empty string.
 *
 * @returns {{ displayNames: string, fullParticipantList: Array<string> }}
 * @example
 *   {
 *     "displayNames": "Rachel Green, Monica Geller +1",
 *     "fullParticipantList": ["Rachel Green", "Monica Geller", ""]
 *   }
 */
export default function getActiveParticipantNames({
  activeParticipantsInConversation,
  referenceName = '',
}: GetActiveParticipantNames): { displayNames: string; fullParticipantList: Array<string> } {
  if (!activeParticipantsInConversation) {
    return { displayNames: referenceName, fullParticipantList: [referenceName] };
  }

  const knownParticipants: Array<string> = [];
  const anonymousParticipants: Array<string> = []; // Anonymous users can be unknown email users (e.g., "Email User") and users with obfuscated names (ex: 3eckpa9ex2708i1ea3skm5e54gc)

  for (const { name } of activeParticipantsInConversation) {
    if (!name || name === UNKNOWN_EMAIL_PARTICIPANT || /\d/.test(name)) {
      anonymousParticipants.push('');
    } else {
      knownParticipants.push(name);
    }
  }

  if (knownParticipants.length === 0) {
    return { displayNames: referenceName, fullParticipantList: [referenceName] };
  }

  const fullParticipantList = [...knownParticipants, ...anonymousParticipants];
  const anonymousUserCount = anonymousParticipants.length;
  const knownParticipantNames = knownParticipants.join(', ');
  const displayNames =
    anonymousUserCount > 0 ? `${knownParticipantNames} +${anonymousUserCount}` : knownParticipantNames;

  return {
    displayNames,
    fullParticipantList,
  };
}
